import { useCallback, useEffect, useState } from 'react';
import '../../backlog/backlog.css';
import '../../modules/module.css';
import './quotes.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar, ToolbarDivider } from '../../../components/AppToolbar';
import { useDeleteFlow } from '../../../components/confirm';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { DateRangeSelect } from '../../modules/DateRangeSelect';
import { fetchOpenQuotes } from './api';
import { QUOTE_COLUMNS } from './columns';
import { deleteQuotes, downloadQuoteReport, reportErrorMessage } from './detailApi';
import QuoteDetailPage from './QuoteDetailPage';
import { useQuoteSearch } from './useQuoteSearch';
import {
  QUOTE_DEFAULT_FILTER, QUOTE_RANGES, rangeCode, rangeDays,
  type QuoteDateFilter, type QuoteListRow,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Accounting ▸ Quotes ▸ Open Quotes (nav `acct-quotes-open`).
// Port of legacy ALPACA `CRMQuotes_ListView_pendingsubmission`
// (BTLIMS.Web/Model.xafml :14104), under the nav group `Accounting\Quotes`.
//
// The quotes still in play: not cancelled, not lapsed, and either Pending
// Submission or Pending Validation — narrowed by the Date Filter chip. Those
// criteria live in `dbo.AI_Quotes_OpenList_SP`, not here, so the screen cannot
// disagree with an export or with any later report (§18.5, one source of truth).
//
// TOOLBAR — legacy: New · Edit · Delete · Preview · Date Filter · Refresh ·
// Export to · Search. The standard actions lead the form-specific ones here, so
// the shared <AppToolbar crudFirst> opt-in is used (owner decision 2026-10-13);
// this is the second form to pass it, after Item Initialization. With
// `crudFirst` the Date Filter sits just before Preview rather than just after
// it — the one difference from the legacy order, accepted rather than adding a
// fourth ordering option to the shared toolbar.
//
// New and Edit open the full-page Open Quote detail view, which REPLACES this
// list (standing rule 12). A row also opens on double-click, as in legacy.
//
// AUDIT. FormName "Open Quotes" — the name legacy already writes, because it
// audits by the nav caption clicked (AuditlogViewController :2065).
// ─────────────────────────────────────────────────────────────────────────────

const FORM_NAME = 'Open Quotes';

type Screen = { kind: 'list' } | { kind: 'detail'; id: string | null; key: number };

export default function OpenQuotesPage({ onHome, pageId = 'acct-quotes-open' }: { onHome?: () => void; pageId?: string }) {
  const notify    = useNotify();
  const runDelete = useDeleteFlow();
  // MAC: New → Create · Edit / Validate / Save → Write · Delete → Delete. Hidden,
  // never greyed; a Read-only role still opens a quote (double-click) to view it.
  const perm      = useNavActions(pageId);
  const [rows, setRows]       = useState<QuoteListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<QuoteDateFilter>(QUOTE_DEFAULT_FILTER);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [screen, setScreen] = useState<Screen>({ kind: 'list' });

  // The toolbar Search box filters the rows already fetched — it does NOT
  // widen the Date Filter, so a quote outside the chosen window stays out
  // until the chip is widened. Same as legacy.
  const shown = useQuoteSearch(rows, search);

  const load = useCallback(async (filter?: QuoteDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchOpenQuotes(filter);
      setRows(res.rows);
      // The chip the SERVER applied, not the one we asked for.
      setDateFilter(res.dateFilter);
      // A reload can drop rows; a surviving selection would point at nothing.
      setSelected(new Set());
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load open quotes.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  function open(id: string | null) {
    // A fresh key every time, so "New" after "New" really is a blank form.
    setScreen({ kind: 'detail', id, key: Date.now() });
  }

  async function deleteSelected() {
    const ids = [...selected].map(String);
    if (ids.length === 0) return;
    const numbers = rows.filter((r) => ids.includes(r.oid)).map((r) => r.quoteID).filter(Boolean);
    await runDelete({
      formName: FORM_NAME,
      message: ids.length === 1 ? `Delete quote ${numbers[0] ?? ''}?` : `Delete ${ids.length} quotes?`,
      // All-or-nothing on the server: if any selected quote is used in sample
      // registration, none is deleted and the refusal carries legacy's wording.
      delete: async () => { await deleteQuotes(ids); },
      onDeleted: () => { void load(dateFilter); },
    });
  }

  /** Preview — legacy `btnqrpreview`, which needs exactly ONE selected quote
   *  (`SelectionDependencyType.RequireSingleObject`). */
  async function preview() {
    if (selected.size !== 1) {
      // Legacy wording, verbatim (LDMMessages "selectchkbox").
      notify.warning('Please select an item to proceed further.');
      return;
    }
    try {
      const filename = await downloadQuoteReport(String([...selected][0]));
      notify.success(`${filename} downloaded.`);
    } catch (e) {
      // A quote with no pricing rows is refused in legacy's words
      // ("Add pricecode and continue.").
      notify.error((await reportErrorMessage(e)) ?? 'Failed to generate the quote report.');
    }
  }

  // ── Detail view (New or Edit) — replaces the list, never a popup (rule 12)
  if (screen.kind === 'detail') {
    return (
      <QuoteDetailPage
        key={screen.key}
        id={screen.id}
        siblingIds={shown.map((r) => r.oid)}
        onNavigate={(id) => open(id)}
        onClose={() => setScreen({ kind: 'list' })}
        onNew={() => open(null)}
        perm={perm}
        onCreated={(id) => open(id)}
        onChanged={() => void load(dateFilter)}
      />
    );
  }

  const one = selected.size === 1 ? String([...selected][0]) : null;

  // The row edit icon every list carries (ModuleListView's `.mlv-rowact`), ahead
  // of the data columns; it opens the quote, as a double-click does.
  const columns: AppColumn<QuoteListRow>[] = [
    ...(perm.write ? [{ field: '__edit', header: '', width: 40, sortable: false, filterable: false, noExport: true,
      render: (r: QuoteListRow) => (
        <button type="button" className="mlv-rowact qt-rowedit" title="Edit" aria-label={`Edit ${r.quoteID ?? 'quote'}`}
                onClick={(e) => { e.stopPropagation(); open(r.oid); }}>
          <i className="ti ti-edit" aria-hidden="true" />
        </button>
      ) } as AppColumn<QuoteListRow>] : []),
    ...QUOTE_COLUMNS,
  ];

  return (
    // The standard list shell, as the reference list (Settings > Inventory
    // Management > Items) lays it out: breadcrumb, title, toolbar, search row and
    // the grid card inside the padded grid area (owner report 2026-10-20).
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          {/* The SAME Open Quotes list (legacy CRMQuotes_ListView_pendingsubmission)
              shows under BOTH Accounting ▸ Quotes AND CRM ▸ Accounts ▸ Quotes — the
              breadcrumb + title follow whichever nav entry was opened (owner request
              2026-10-22). */}
          {pageId === 'crm-quotes-open' ? (
            <>
              <span>CRM</span>
              <i className="ti ti-chevron-right" />
              <span>Accounts</span>
              <i className="ti ti-chevron-right" />
              <span>Quotes</span>
            </>
          ) : (
            <>
              <span>Accounting</span>
              <i className="ti ti-chevron-right" />
              <span>Quotes</span>
            </>
          )}
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Open Quotes</span>
        </div>
      <FormTitle pageId={pageId} loadMs={loadMs} loading={loading}
                 auditFormName={FORM_NAME} />
      <AppToolbar
        crudFirst
        onNew={perm.create ? () => open(null) : undefined}
        onEdit={perm.write ? () => { if (one) open(one); } : undefined}
        editDisabled={!one}
        onDelete={perm.delete ? () => void deleteSelected() : undefined}
        deleteDisabled={selected.size === 0}
        // Date Filter as a dropdown between the actions and Refresh — the same
        // shared control and place Sample Registration uses (owner request
        // 2026-10-20), and legacy's order (… Preview · Date Filter · Refresh).
        beforeRefresh={
          <>
            <ToolbarDivider />
            <DateRangeSelect
              variant="dropdown" label="Date Filter" ranges={QUOTE_RANGES}
              value={rangeDays(dateFilter)}
              onChange={(days) => { const c = rangeCode(days); setDateFilter(c); void load(c); }}
            />
          </>
        }
        onPreview={() => void preview()}
        previewDisabled={selected.size !== 1}
        onRefresh={() => load(dateFilter)}
        refreshing={loading}
        exportData={{ filename: 'open-quotes', ...gridExportMatrix(shown, QUOTE_COLUMNS) }}
      />
      <GridSearch value={search} onChange={setSearch} placeholder="Text to search..." />
      <AppDataGrid<QuoteListRow>
        rows={shown}
        columns={columns}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onRowDoubleClick={(r) => open(r.oid)}
        defaultPageSize={20}
        emptyText={search ? 'No results match your search.' : 'No open quotes for this date range.'}
      />
      </div>
    </div>
  );
}
